import { useEffect, useMemo, useState } from 'react';
import { Col, Row, Space } from 'antd';
import { FastField, FieldProps, FormikProvider, useFormik } from 'formik';
import { EditOutlined } from '@ant-design/icons';
import ConfirmationModal from 'components/new-ui/composed/modal/confirmation-modal/ConfirmationModal';
import FormInput from 'components/new-ui/composed/formInput/FormInput';
import FormSelect from 'components/new-ui/composed/formSelect/FormSelect';
import { TransportType, TransportTypeOptions } from 'utils/transportTypes';
import { RefWrapper } from 'components/new-ui/composed/formSelect/styles';
import CountrySelect from 'components/new-ui/composed/selects/CountrySelect';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import useCodelists from 'hooks/useCodelists';
import FormDatePicker from 'components/new-ui/composed/formDatePicker/FormDatePicker';
import { Manifest, ManifestDeclarationsResponse } from 'store/manifests/types';
import { UpdateManifestFormFields, updateManifestValidationSchema } from './update-manifest-validation.schema';
import ContainerStatusField from '../create-manifest-form/components/ContainerStatusField';
import { ContainerStatuses, UK_COUNTRY_CODE } from '../create-manifest-form/types';
import { getErrorMessage } from 'utils/getErrorMessage';
import Notification from 'components/ui/base/notification/Notification';
import useManifests from 'hooks/useManifests';
import useGlobalOverlay from 'hooks/useGlobalOverlay';
import useCountries from 'hooks/useCountries';
import LabelRequiredSymbol from 'components/new-ui/base/label/LabelRequiredSymbol';
import UnLocodeSelect from 'components/new-ui/composed/selects/UnLocodeSelect';
import { ManifestStatuses } from 'views/manifests/types/manifest-statuses';
import WarningMessage from './WarningMessage';
import AmendmentSummaryModal from './AmendmentSummaryModal';
import { AmendmentChange, fieldLabels, getChangedFields, vehicleRegistrationLabels, SelectOptionsMap } from './utils';
import moment from 'moment';

type Props = {
    open: boolean;
    manifest: Manifest;
    jobsWithNonAmendableDeclarations?: ManifestDeclarationsResponse[];
    requiresAmendment?: boolean;
    onClose?: () => void;
    onSuccess?: () => void;
};

/** Backend expects LocalDateTime (no timezone offset). */
const toLocalDateTime = (value: string | moment.Moment | Date | null | undefined) =>
    value ? moment(value).format('YYYY-MM-DDTHH:mm:ss') : null;

const buildDeclarationsData = (values: UpdateManifestFormFields) => ({
    transportId: values.transportTypeId as TransportType,
    departureCountryId: values.countryOfDispatch,
    arrivalPort: values.customsOfficeOfPresentation,
    meansIdentificationType: values.meansIdentificationType,
    meansOfTransportOnArrivalIdNumber: values.meansId,
    nationalityTransportMeans: values.nationalityTransportMeans,
    vehicleRegistration: values.vehicleRegistration,
    dateTimeOfDeparture: toLocalDateTime(values.departureDateTime),
    estimatedDateTimeOfArrival: toLocalDateTime(values.arrivalDateTime) as string,
    containerIndicator: values.containerIndication,
});

const backendFieldToFormikField: Record<string, keyof UpdateManifestFormFields> = {
    'declarationsData.estimatedDateTimeOfArrival': 'arrivalDateTime',
    'declarationsData.dateTimeOfDeparture': 'departureDateTime',
    haulier: 'carrierOrHaulier',
};

const EditManifestFormModal = ({
    open,
    manifest,
    jobsWithNonAmendableDeclarations,
    requiresAmendment,
    onClose,
    onSuccess,
}: Props) => {
    const { updateManifest } = useManifests();
    const { aisCodelists, getIrelandCodelists } = useCodelists();
    const { showGlobalOverlay, hideGlobalOverlay } = useGlobalOverlay();
    const { countries } = useCountries();

    const [disabledDeparturePort, setDisabledDeparturePort] = useState(false);
    const [showAmendmentSummaryModal, setShowAmendmentSummaryModal] = useState(false);
    const [amendmentChanges, setAmendmentChanges] = useState<AmendmentChange[]>([]);

    const { referenceId, declarationsData, haulier, departurePort } = manifest;

    const meansIdentityTypeOptions = useMemo(
        () =>
            aisCodelists?.meansIdentityType
                ? [...aisCodelists.meansIdentityType].sort((a, b) => Number(a.id) - Number(b.id))
                : [],
        [aisCodelists?.meansIdentityType]
    );

    const initialValues = useMemo<UpdateManifestFormFields>(() => {
        const {
            estimatedDateTimeOfArrival,
            arrivalPort,
            containerIndicator,
            dateTimeOfDeparture,
            departureCountryId,
            meansIdentificationType,
            meansOfTransportOnArrivalIdNumber,
            nationalityTransportMeans,
            transportId,
            vehicleRegistration,
        } = declarationsData || {};

        return {
            arrivalDateTime: estimatedDateTimeOfArrival ?? null,
            carrierOrHaulier: haulier ?? '',
            containerIndication: containerIndicator ? ContainerStatuses.ACCOMPANIED : ContainerStatuses.UNACOOMPANIED,
            countryOfDispatch: departureCountryId ?? undefined,
            customsOfficeOfPresentation: arrivalPort ?? undefined,
            departureDateTime: dateTimeOfDeparture ?? null,
            meansId: meansOfTransportOnArrivalIdNumber ?? '',
            meansIdentificationType: meansIdentificationType ?? undefined,
            nationalityTransportMeans: nationalityTransportMeans ?? undefined,
            referenceId: referenceId ?? '',
            vehicleRegistration,
            departurePort,
            transportTypeId: transportId ?? undefined,
        };
    }, [declarationsData, haulier, referenceId, departurePort]);

    const isActive = manifest.status === ManifestStatuses.ACTIVE;

    // When the manifest is in DRAFT status
    const handleEditManifest = async () => {
        showGlobalOverlay({
            type: 'LoadingOverlay',
            payload: {
                message: 'Updating manifest',
            },
        });
        try {
            const { referenceId, carrierOrHaulier, departurePort } = formik.values;

            await updateManifest(manifest.id, {
                haulier: carrierOrHaulier,
                referenceId,
                departurePort,
                declarationsData: buildDeclarationsData(formik.values),
            });

            Notification({
                type: 'success',
                messageTitle: 'Manifest Updated',
                description: 'Successfully updated manifest.',
                placement: 'top',
            });

            onSuccess?.();
        } catch (err: any) {
            const data = err?.response?.data ?? err;
            if (applySubErrors(err)) {
                Notification({
                    type: 'error',
                    messageTitle: 'Update Error',
                    description: data.message || 'Some fields are invalid. Please fix the highlighted fields.',
                    placement: 'top',
                });
            } else {
                Notification({
                    type: 'error',
                    messageTitle: 'Update Error',
                    description: getErrorMessage(err) || 'Could not update manifest. Please try again.',
                    placement: 'top',
                });
            }
        } finally {
            hideGlobalOverlay();
        }
    };

    const handleSubmitBulkAmendment = async (amendmentReason: string | undefined) => {
        showGlobalOverlay({ type: 'LoadingOverlay', payload: { message: 'Submitting bulk amendment' } });
        try {
            const { referenceId, carrierOrHaulier, departurePort } = formik.values;

            await updateManifest(manifest.id, {
                haulier: carrierOrHaulier,
                referenceId,
                departurePort,
                amendmentReason,
                declarationsData: buildDeclarationsData(formik.values),
            });

            const notificationTitle = requiresAmendment ? 'Bulk Amendment Submitted' : 'Manifest Updated';
            const notificationDescription = requiresAmendment
                ? 'Successfully submitted bulk amendment.'
                : 'Successfully updated manifest.';

            Notification({
                type: 'success',
                messageTitle: notificationTitle,
                description: notificationDescription,
                placement: 'top',
            });

            setShowAmendmentSummaryModal(false);
            onSuccess?.();
        } catch (err: any) {
            const data = err?.response?.data ?? err;

            if (applySubErrors(err)) {
                setShowAmendmentSummaryModal(false);
                Notification({
                    type: 'error',
                    messageTitle: 'Amendment Error',
                    description: data.message || 'Some fields are invalid. Please fix the highlighted fields.',
                    placement: 'top',
                });
            } else {
                Notification({
                    type: 'error',
                    messageTitle: 'Amendment Error',
                    description: getErrorMessage(err) || 'Could not submit bulk amendment. Please try again.',
                    placement: 'top',
                });
            }
        } finally {
            hideGlobalOverlay();
        }
    };

    // When the manifest is in ACTIVE status
    const handleBulkAmendment = async () => {
        const countryOptions = countries.list.map((c) => ({ id: c.countryCode, value: c.name }));

        const selectOptionsMap: SelectOptionsMap = {
            transportTypeId: TransportTypeOptions,
            meansIdentificationType: aisCodelists?.meansIdentityType,
            customsOfficeOfPresentation: aisCodelists?.nationalCustomsOffice,
            countryOfDispatch: countryOptions,
            nationalityTransportMeans: countryOptions,
        };

        const changes = getChangedFields(formik.initialValues, formik.values, selectOptionsMap);
        setAmendmentChanges(changes);
        setShowAmendmentSummaryModal(true);
    };

    const formik = useFormik<UpdateManifestFormFields>({
        initialValues,
        validationSchema: updateManifestValidationSchema,
        enableReinitialize: true,
        validateOnChange: false,
        validateOnMount: true,
        onSubmit: async (_, helpers) => {
            await helpers.validateForm();
            return isActive ? handleBulkAmendment() : handleEditManifest();
        },
    });

    const applySubErrors = (err: any): boolean => {
        const data = err?.response?.data ?? err;
        if (!data?.subErrors?.length) return false;

        const formikErrors: Partial<Record<keyof UpdateManifestFormFields, string>> = {};
        const touched: Partial<Record<keyof UpdateManifestFormFields, boolean>> = {};

        data.subErrors.forEach((subErr: any) => {
            const formikField = backendFieldToFormikField[subErr.field];
            if (formikField) {
                formikErrors[formikField] = subErr.message || data.message;
                touched[formikField] = true;
            }
        });

        formik.setErrors(formikErrors);
        formik.setTouched(touched, false);
        return true;
    };

    const isAccompanied = useMemo(
        () => formik.values.containerIndication === ContainerStatuses.ACCOMPANIED,
        [formik.values.containerIndication]
    );

    const { getFieldHelpers, getFieldMeta, getFieldProps } = formik;

    // For now we only handle AIS in manfiests
    useEffect(() => {
        getIrelandCodelists(DeclarationTypes.AIS);
    }, [getIrelandCodelists]);

    // Reset the form when the modal opens and display errors
    useEffect(() => {
        if (open) {
            formik.resetForm({ values: initialValues });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialValues]);

    // If the country of dispatch is different than UK,
    // reset and disable the departure port; restore to original when switching back
    useEffect(() => {
        if (formik.values.countryOfDispatch !== UK_COUNTRY_CODE) {
            formik.setFieldValue('departurePort', undefined);
            setDisabledDeparturePort(true);
        } else {
            formik.setFieldValue('departurePort', formik.initialValues.departurePort);
            setDisabledDeparturePort(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.countryOfDispatch]);

    return (
        <>
            <ConfirmationModal
                maskClosable={false}
                open={open && !showAmendmentSummaryModal}
                width={760}
                icon={<EditOutlined />}
                title="Edit Manifest Details"
                onCancel={onClose}
                onConfirm={formik.handleSubmit}
                confirmButtonText={formik.isSubmitting ? 'Saving...' : 'Save Changes'}
                confirmationButtonDisabled={!formik.dirty}
                confirmationButtonLoading={formik.isSubmitting}
                content={
                    <FormikProvider value={formik}>
                        {isActive && requiresAmendment && <WarningMessage />}

                        <div style={{ width: '100%' }}>
                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <label htmlFor="referenceId">
                                        <Space size={8}>
                                            <LabelRequiredSymbol />
                                            {fieldLabels.referenceId}:
                                        </Space>
                                    </label>
                                </Col>
                                <Col span={14}>
                                    <FastField name="referenceId">
                                        {({ field, meta, form }: FieldProps) => (
                                            <FormInput
                                                required
                                                placeholder="Enter manifest ID"
                                                fieldProps={field}
                                                fieldMeta={meta}
                                                fieldHelper={form.getFieldHelpers('referenceId')}
                                                disabled
                                            />
                                        )}
                                    </FastField>
                                </Col>
                            </Row>
                        </div>

                        <Space direction="vertical" size={12} style={{ width: '100%' }}>
                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <label>
                                        <Space size={8}>
                                            <RefWrapper>7.2</RefWrapper>
                                            <LabelRequiredSymbol />
                                            {fieldLabels.containerIndication}:
                                        </Space>
                                    </label>
                                </Col>
                                <Col span={14}>
                                    <FastField name="containerIndication">
                                        {({ field, meta, form }: FieldProps) => (
                                            <ContainerStatusField
                                                style={{ width: '100%' }}
                                                fieldProps={field}
                                                fieldMeta={meta}
                                                fieldHelpers={form.getFieldHelpers('containerIndication')}
                                            />
                                        )}
                                    </FastField>
                                </Col>
                            </Row>

                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <label htmlFor="transportTypeId">
                                        <Space size={8}>
                                            <RefWrapper>7.4</RefWrapper>
                                            <LabelRequiredSymbol />
                                            {fieldLabels.transportTypeId}:
                                        </Space>
                                    </label>
                                </Col>
                                <Col span={14}>
                                    <FastField name="transportTypeId">
                                        {({ field, meta, form }: FieldProps) => (
                                            <FormSelect
                                                placeholder="Select transport type"
                                                fieldProps={field}
                                                fieldMeta={meta}
                                                fieldHelper={form.getFieldHelpers('transportTypeId')}
                                                selectOptions={TransportTypeOptions}
                                            />
                                        )}
                                    </FastField>
                                </Col>
                            </Row>

                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <label htmlFor="carrierOrHaulier">{fieldLabels.carrierOrHaulier}:</label>
                                </Col>
                                <Col span={14}>
                                    <FastField name="carrierOrHaulier">
                                        {({ field, meta, form }: FieldProps) => (
                                            <FormInput
                                                placeholder="Enter Carrier/Haulier"
                                                fieldProps={field}
                                                fieldMeta={meta}
                                                fieldHelper={form.getFieldHelpers('carrierOrHaulier')}
                                            />
                                        )}
                                    </FastField>
                                </Col>
                            </Row>

                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <label htmlFor="countryOfDispatch">
                                        <Space size={8}>
                                            <RefWrapper>5.14</RefWrapper>
                                            <LabelRequiredSymbol />
                                            {fieldLabels.countryOfDispatch}:
                                        </Space>
                                    </label>
                                </Col>
                                <Col span={14}>
                                    <FastField name="countryOfDispatch">
                                        {({ field, meta, form }: FieldProps) => (
                                            <CountrySelect
                                                hideLabel
                                                placeholder="Select country of dispatch"
                                                fieldProps={field}
                                                fieldMeta={meta}
                                                fieldHelper={form.getFieldHelpers('countryOfDispatch')}
                                            />
                                        )}
                                    </FastField>
                                </Col>
                            </Row>

                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <Space>
                                        <label htmlFor="departurePort">{fieldLabels.departurePort}:</label>
                                        <LabelRequiredSymbol />
                                    </Space>
                                </Col>
                                <Col span={14}>
                                    <UnLocodeSelect
                                        disabled={disabledDeparturePort}
                                        countryCode={UK_COUNTRY_CODE}
                                        placeholder="Select departure port"
                                        fieldProps={getFieldProps('departurePort')}
                                        fieldMeta={getFieldMeta('departurePort')}
                                        fieldHelper={getFieldHelpers('departurePort')}
                                    />
                                </Col>
                            </Row>

                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <label htmlFor="customsOfficeOfPresentation">
                                        <Space size={8}>
                                            <RefWrapper>5.26</RefWrapper>
                                            <LabelRequiredSymbol />
                                            {fieldLabels.customsOfficeOfPresentation}:
                                        </Space>
                                    </label>
                                </Col>
                                <Col span={14}>
                                    <FastField name="customsOfficeOfPresentation">
                                        {({ field, meta, form }: FieldProps) => (
                                            <FormSelect
                                                placeholder="Select customs office of presentation"
                                                fieldProps={field}
                                                fieldMeta={meta}
                                                fieldHelper={form.getFieldHelpers('customsOfficeOfPresentation')}
                                                selectOptions={aisCodelists?.nationalCustomsOffice}
                                            />
                                        )}
                                    </FastField>
                                </Col>
                            </Row>

                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <label htmlFor="meansIdentificationType">
                                        <Space size={8}>
                                            <RefWrapper>7.9</RefWrapper>
                                            <LabelRequiredSymbol />
                                            {fieldLabels.meansIdentificationType}:
                                        </Space>
                                    </label>
                                </Col>
                                <Col span={14}>
                                    <FastField name="meansIdentificationType">
                                        {({ field, meta, form }: FieldProps) => (
                                            <FormSelect
                                                placeholder="Select transport means ID type"
                                                selectOptions={meansIdentityTypeOptions}
                                                fieldProps={field}
                                                fieldMeta={meta}
                                                fieldHelper={form.getFieldHelpers('meansIdentificationType')}
                                            />
                                        )}
                                    </FastField>
                                </Col>
                            </Row>

                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <label htmlFor="meansId">
                                        <Space size={8}>
                                            <RefWrapper>7.9</RefWrapper>
                                            <LabelRequiredSymbol />
                                            {fieldLabels.meansId}:
                                        </Space>
                                    </label>
                                </Col>
                                <Col span={14}>
                                    <FastField name="meansId">
                                        {({ field, meta, form }: FieldProps) => (
                                            <FormInput
                                                placeholder="Enter means of transport ID"
                                                fieldProps={field}
                                                fieldMeta={meta}
                                                fieldHelper={form.getFieldHelpers('meansId')}
                                            />
                                        )}
                                    </FastField>
                                </Col>
                            </Row>

                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <label htmlFor="nationalityTransportMeans">
                                        <Space size={8}>
                                            <RefWrapper>7.15</RefWrapper>
                                            <LabelRequiredSymbol />
                                            {fieldLabels.nationalityTransportMeans}:
                                        </Space>
                                    </label>
                                </Col>
                                <Col span={14}>
                                    <FastField name="nationalityTransportMeans">
                                        {({ field, meta, form }: FieldProps) => (
                                            <CountrySelect
                                                hideLabel
                                                placeholder="Select nationality transport means"
                                                fieldProps={field}
                                                fieldMeta={meta}
                                                fieldHelper={form.getFieldHelpers('nationalityTransportMeans')}
                                            />
                                        )}
                                    </FastField>
                                </Col>
                            </Row>

                            {isAccompanied ? (
                                <Row justify="space-between" gutter={6} align="middle">
                                    <Col span={10}>
                                        <label htmlFor="roroImoNumber">
                                            <Space size={8}>
                                                <RefWrapper>1D94</RefWrapper>
                                                {vehicleRegistrationLabels.accompanied}:
                                            </Space>
                                        </label>
                                    </Col>
                                    <Col span={14}>
                                        <FastField name="vehicleRegistration">
                                            {({ field, meta, form }: FieldProps) => (
                                                <FormInput
                                                    placeholder="Enter IMO"
                                                    fieldProps={field}
                                                    fieldMeta={meta}
                                                    fieldHelper={form.getFieldHelpers('vehicleRegistration')}
                                                />
                                            )}
                                        </FastField>
                                    </Col>
                                </Row>
                            ) : (
                                <Row justify="space-between" gutter={6} align="middle">
                                    <Col span={10}>
                                        <label htmlFor="trailerId">
                                            <Space size={8}>
                                                <RefWrapper>1D95</RefWrapper>
                                                {vehicleRegistrationLabels.unaccompanied}:
                                            </Space>
                                        </label>
                                    </Col>
                                    <Col span={14}>
                                        <FastField name="vehicleRegistration">
                                            {({ field, meta, form }: FieldProps) => (
                                                <FormInput
                                                    placeholder="Enter Trailer ID"
                                                    fieldProps={field}
                                                    fieldMeta={meta}
                                                    fieldHelper={form.getFieldHelpers('vehicleRegistration')}
                                                />
                                            )}
                                        </FastField>
                                    </Col>
                                </Row>
                            )}

                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <label htmlFor="departureDateTime">
                                        <Space size={8}>
                                            <RefWrapper>1D23</RefWrapper>
                                            {fieldLabels.departureDateTime}:
                                        </Space>
                                    </label>
                                </Col>
                                <Col span={14}>
                                    <FormDatePicker
                                        showTime
                                        placeholder="Select date and time of departure"
                                        fieldName="departureDateTime"
                                        serializeDate={(value) => toLocalDateTime(value) ?? ''}
                                    />
                                </Col>
                            </Row>

                            <Row justify="space-between" gutter={6} align="middle">
                                <Col span={10}>
                                    <label htmlFor="arrivalDateTime">
                                        <Space size={8}>
                                            <RefWrapper>1D24</RefWrapper>
                                            <LabelRequiredSymbol />
                                            {fieldLabels.arrivalDateTime}:
                                        </Space>
                                    </label>
                                </Col>
                                <Col span={14}>
                                    <FormDatePicker
                                        showTime
                                        placeholder="Select date and time of arrival"
                                        fieldName="arrivalDateTime"
                                        serializeDate={(value) => toLocalDateTime(value) ?? ''}
                                    />
                                </Col>
                            </Row>
                        </Space>
                    </FormikProvider>
                }
            />

            <AmendmentSummaryModal
                data={jobsWithNonAmendableDeclarations}
                open={showAmendmentSummaryModal}
                changes={amendmentChanges}
                requiresAmendment={requiresAmendment}
                onClose={() => setShowAmendmentSummaryModal(false)}
                onConfirm={handleSubmitBulkAmendment}
            />
        </>
    );
};

export default EditManifestFormModal;
